import { GetServerSidePropsContext, NextPage } from 'next';
import { useRouter } from 'next/router';

import StaticPageComponent from 'src/components/staticPageComponent/StaticPageComponent';
import StaticTempComponent from 'src/components/staticPageComponent/StaticTempComponent';
import { useAppStore } from 'src/hooks/useAppStore';
import { fetchStaticPageData } from 'src/services/apiRequests/StaticPageService';
import SimpleStore from 'src/store/SimpleStore';
import { HTTP_URL_PARAMS } from 'src/utils/constants/HttpMethodsConstants';
import {
  CountryCode,
  DisplayLanguage,
  StaticPageConstants
} from 'src/utils/constants/stringConstants';

import SEOMeta from '../components/pageMeta/SEOMeta';
import {
  getSeoData,
  replaceMetaData
} from '../components/pageMeta/SEOMetaUtils';
import { DynamicObject } from '../utils/commonTypes';

const getStaticPageType = (pageLink: string) => {
  switch (pageLink) {
    case StaticPageConstants.ABOUT_US_PAGE:
      return StaticPageConstants.ABOUT_US;
    case StaticPageConstants.PRIVACY_POLICIES_PAGE:
      return StaticPageConstants.PRIVACY_POLICIES;
    case StaticPageConstants.TERMS_OF_USE_PAGE:
      return StaticPageConstants.TERMS_OF_USE;
    case StaticPageConstants.TERMS_CONDITIONS_PAGE:
      return StaticPageConstants.TERMS_CONDITIONS_TEXT;
    case StaticPageConstants.HOW_TO_PLAY_PAGE:
      return StaticPageConstants.HOW_TO_PLAY_TEXT;
    case StaticPageConstants.ZEE5_COMMUNITY_GUIDELINES_PAGE:
      return StaticPageConstants.ZEE5_COMMUNITY_GUIDELINES_TEXT;
    case StaticPageConstants.REWARDS_TERM_AND_CONDITIONS_PAGE:
      return StaticPageConstants.REWARDS_TERM_AND_CONDITIONS_TEXT;
    case StaticPageConstants.REWARDS_HOW_IT_WORKS_PAGE:
      return StaticPageConstants.REWARDS_HOW_IT_WORKS_TEXT;
    default:
      return '';
  }
};

const getStaticTextType = (staticPageType: string) => {
  switch (staticPageType) {
    case StaticPageConstants.ABOUT_US:
      return StaticPageConstants.ABOUT_US_PARAM;
    case StaticPageConstants.PRIVACY_POLICIES:
      return StaticPageConstants.PRIVACY_POLICIES_PARAM;
    case StaticPageConstants.TERMS_OF_USE:
      return StaticPageConstants.TERMS_OF_USE_PARAM;
    default:
      return '';
  }
};

const getStaticPageBreadCrumb = (
  pageLink: string,
  translations: DynamicObject
) => {
  if (!translations || !Object.keys(translations).length) {
    return [];
  }

  switch (pageLink) {
    case StaticPageConstants.ABOUT_US_PAGE:
      return [
        {
          label: translations.BREADCRUMB.ABOUTUS || '',
          url: `/aboutus`
        }
      ];
    case StaticPageConstants.PRIVACY_POLICIES_PAGE:
      return [
        {
          label: translations.BREADCRUMB.PRIVACY || '',
          url: '/privacypolicy'
        }
      ];
    case StaticPageConstants.TERMS_OF_USE_PAGE:
      return [
        {
          label: translations.BREADCRUMB.TERMS || '',
          url: '/termsofuse'
        }
      ];
    default:
      return [];
  }
};
interface MetaData {
  title: string;
  description: string;
}

interface PageProps {
  pageText: string;
  metaData: MetaData;
  staticPage: string;
}

const getStaticPageData = async (
  countryCode: string,
  staticPageType: string,
  language: DisplayLanguage
) => {
  const responseData = await fetchStaticPageData(
    countryCode,
    getStaticTextType(staticPageType),
    language
  );
  if (responseData && responseData[`${staticPageType}_pwa`]) {
    const staticPageData = responseData[`${staticPageType}_pwa`];
    return staticPageData;
  }
  return '';
};

/*
 * This is a static page for the following:
 * 1. termsofuse
 * 2. privacypolicy
 * 3. aboutus
 */

const StaticPage: NextPage<PageProps> = ({
  pageText,
  metaData: metaDataRaw,
  staticPage
}) => {
  const { translations, usersPreferenceData, initialLaunchData } =
    useAppStore();
  const router = useRouter();

  const allDisplayLanguages = initialLaunchData?.getlang.display ?? [];
  const consumptionPageTranslation = translations?.consumption;
  const displayLanguage =
    usersPreferenceData?.display_language ?? DisplayLanguage.ENGLISH;

  const pageQuery =
    (router.query.staticPage && router.query.staticPage[0]) || '';

  const breadcrumbs = getStaticPageBreadCrumb(
    pageQuery,
    consumptionPageTranslation
  );
  const metaData = replaceMetaData({
    metaDataObj: metaDataRaw,
    languages: allDisplayLanguages,
    languageCode: displayLanguage
  });

  return (
    <>
      {pageText ? (
        <>
          <StaticPageComponent staticPageText={pageText} />
          <SEOMeta
            isConsumption={false}
            pageMeta={metaData}
            apiData={undefined}
            seoMetaObj={undefined}
            breadcrumbs={breadcrumbs}
            organizationSchemaData={true}
          />
        </>
      ) : (
        <StaticTempComponent pageQuery={staticPage} />
      )}
    </>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const countryCode =
    SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE) || CountryCode.IN;

  const pathName = decodeURI(context.req?.url as string);

  const locale = context.locale || DisplayLanguage.ENGLISH;

  const pageQuery =
    (context.query.staticPage && context.query.staticPage[0]) || '';

  /**
   * Static page logic below is applicable for static pages launched for ILT only.
   * It is not required for About Us, Privacy Policy and terms and condition page of zee5.com.
   */
  let staticPage = '';
  if (context?.params?.staticPage?.[0] === 'termsandconditions') {
    staticPage = 'termsandconditions';
  } else if (context?.params?.staticPage?.[0] === 'howtoplay') {
    staticPage = 'howtoplay';
  } else if (context?.params?.staticPage?.[0] === 'zee5communityguidelines') {
    staticPage = 'zee5communityguidelines';
  } else if (context?.params?.staticPage?.[0] === 'zeerewardshowitworks') {
    staticPage = 'zeerewardshowitworks';
  } else if (
    context?.params?.staticPage?.[0] === 'zeerewardstermsandconditions'
  ) {
    staticPage = 'zeerewardstermsandconditions';
  }
  const staticPageLinksList = [
    StaticPageConstants.ABOUT_US_PAGE,
    StaticPageConstants.PRIVACY_POLICIES_PAGE,
    StaticPageConstants.TERMS_OF_USE_PAGE,
    StaticPageConstants.TERMS_CONDITIONS_PAGE,
    StaticPageConstants.HOW_TO_PLAY_PAGE,
    StaticPageConstants.ZEE5_COMMUNITY_GUIDELINES_PAGE,
    StaticPageConstants.REWARDS_TERM_AND_CONDITIONS_PAGE,
    StaticPageConstants.REWARDS_HOW_IT_WORKS_PAGE
  ];

  let pageText = '';
  if (
    pageQuery &&
    typeof pageQuery === 'string' &&
    staticPageLinksList.find((page) => page === pageQuery)
  ) {
    pageText = await getStaticPageData(
      countryCode,
      getStaticPageType(pageQuery),
      locale as DisplayLanguage
    );
  }
  if (
    !pageText &&
    pageQuery !== 'termsandconditions' &&
    pageQuery !== 'howtoplay' &&
    pageQuery !== 'zee5communityguidelines' &&
    pageQuery !== 'zeerewardshowitworks' &&
    pageQuery !== 'zeerewardstermsandconditions'
  ) {
    return {
      redirect: {
        destination: '/pagenotfound',
        permanent: true
      }
    };
  }

  const seoMetaAssets: DynamicObject = await getSeoData(
    countryCode,
    SimpleStore.get(HTTP_URL_PARAMS.DISPLAY_LANGUAGE) ??
      DisplayLanguage.ENGLISH,
    pathName
  );

  /**
   * We are getting inline styles in the response from the api
   * This regex is being used to remove inline styles
   */
  const inlinStylesfilter = /(style="[^"]*")/gi;
  // const inlinStylesfilter = /(style=")([a-zA-Z0-9:;\\.\s\\(\\)\-\\,]*)(")/gi;

  const filteredText = pageText.replaceAll(inlinStylesfilter, '');
  const metaData = pageQuery.includes(StaticPageConstants.PRIVACY_POLICIES_PAGE)
    ? seoMetaAssets?.['/']
    : seoMetaAssets?.[`/${pageQuery}`] || null;
  return { props: { pageText: filteredText, metaData, staticPage } };
};

export default StaticPage;
